import { Link } from "react-router-dom";
import { Plane, Clock } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IMAGES } from "@/lib/images";
import { FRANCE_CITIES, EUROPE_CITIES } from "@/lib/fleet";
import { Reveal, SectionHead } from "./Reveal";

export const Destinations = () => {
  const { t } = useI18n();
  return (
    <section id="destinations" className="relative border-y border-white/5 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.monaco} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C0E] via-[#0B0C0E]/80 to-[#0B0C0E]" />
      </div>
      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <SectionHead eyebrow={t("destinations.eyebrow")} title={t("destinations.title")} />
        <Reveal delay={0.1}>
          <p className="mt-6 text-sm font-light text-[var(--elt-muted)] max-w-xl leading-relaxed">{t("destinations.body")}</p>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 gap-14">
          {[
            { label: t("destinations.france"), cities: FRANCE_CITIES, more: t("destinations.franceMore"), tid: "france" },
            { label: t("destinations.europe"), cities: EUROPE_CITIES, more: t("destinations.europeMore"), tid: "europe" },
          ].map((g, gi) => (
            <Reveal key={g.tid} delay={gi * 0.12}>
              <div data-testid={`destinations-${g.tid}`}>
                <p className="font-serif-elt italic text-2xl text-[var(--elt-gold-light)] mb-6">{g.label}</p>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  {g.cities.map((c) => (
                    <span key={c} className="font-serif-elt text-lg font-light text-[var(--elt-ivory)]/85 hover:text-[var(--elt-gold-light)] transition-colors duration-300 cursor-default">{c}</span>
                  ))}
                </div>
                <p className="mt-5 text-xs italic font-light text-[var(--elt-muted)]">{g.more}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.25}>
          <p className="mt-20 text-center font-serif-elt text-xl md:text-2xl tracking-[0.15em] text-[var(--elt-gold)]">{t("destinations.statement")}</p>
        </Reveal>
      </div>
    </section>
  );
};

export const Aviation = () => {
  const { t } = useI18n();
  return (
    <section className="max-w-[1440px] mx-auto px-6 lg:px-10 py-28 lg:py-36 grid lg:grid-cols-12 gap-14 items-center">
      <div className="lg:col-span-6">
        <SectionHead eyebrow={t("aviation.eyebrow")} title={t("aviation.title")} />
        <Reveal delay={0.12}>
          <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-3">
            {t("aviation.items").map((s) => (
              <div key={s} className="flex items-center gap-3 text-sm font-light text-[var(--elt-ivory)]/85 py-1.5 border-b border-white/5">
                <Plane size={12} strokeWidth={1.3} className="text-[var(--elt-gold)] shrink-0" />{s}
              </div>
            ))}
          </div>
          <div className="mt-10 flex items-center gap-3 text-[0.68rem] tracking-[0.3em] uppercase text-[var(--elt-gold)]">
            <Clock size={14} strokeWidth={1.3} /> {t("misc.available247")}
          </div>
          <p className="mt-3 text-xs font-light text-[var(--elt-muted)] max-w-md">{t("misc.availBody")}</p>
          <Link to="/request" data-testid="aviation-request-btn" className="btn-ghost mt-8">{t("cta.request")}</Link>
        </Reveal>
      </div>
      <div className="lg:col-span-5 lg:col-start-8">
        <Reveal delay={0.15}>
          <div className="relative">
            <img src={IMAGES.aviation} alt="Private aviation" className="w-full aspect-[4/3] object-cover" />
            <div className="absolute inset-0 border border-[var(--elt-line)] m-4 pointer-events-none" />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
